import { type PresencePalette, rgba } from "./palette";

/**
 * Waveform-ul: două aripi simetrice care pornesc de la marginea orbului și se
 * sting spre exterior. În prototip era scris, dar închis cu un flag
 * (`WAVE_ON = false`), cu nota „se reactivează odată cu vocea". Asta e.
 *
 * Diferența față de prototip nu e vizuală, e de sursă: acolo `env` era zgomot
 * generat, aici e RMS-ul audio-ului real (vezi `energy.ts`). Deci aripile chiar
 * urmăresc silabele - ale vizitatorului în `listening`, ale asistentului în
 * `speaking`.
 */

/** Puncte pe fiecare aripă. */
const STEPS = 90;

/** Sub atâta energie nu desenăm nimic: liniștea trebuie să arate a liniște. */
const SILENCE = 0.09;

export interface WaveFrame {
  palette: PresencePalette;
  /** Centrul, de regulă același cu al orbului. */
  cx: number;
  cy: number;
  /** Unde începe aripa - raza curentă a orbului plus un mic spațiu. */
  startOffset: number;
  /** Cât de departe se întinde, în px. */
  half: number;
  energy: number;
  /** Timpul în ms - conduce deplasarea ondulației. */
  t: number;
}

export function drawWaveform(ctx: CanvasRenderingContext2D, f: WaveFrame): void {
  if (f.energy <= SILENCE) return;
  const { palette: p, cx, cy, energy, t } = f;
  // Intrarea nu e bruscă: între prag și 0.12 aripile se ridică din nimic.
  const fade = Math.min(1, energy / 0.12);

  for (const dir of [-1, 1]) {
    const grad = ctx.createLinearGradient(cx + dir * f.startOffset, 0, cx + dir * (f.startOffset + f.half), 0);
    grad.addColorStop(0, rgba(p.wave, 0.75 * fade));
    grad.addColorStop(0.55, rgba(p.wave, 0.28 * fade));
    grad.addColorStop(1, rgba(p.wave, 0));

    // Două treceri, oglindite pe verticală: dau grosime fără să dubleze desenul.
    for (let pass = 0; pass < 2; pass++) {
      ctx.beginPath();
      for (let i = 0; i <= STEPS; i++) {
        const frac = i / STEPS;
        const x = cx + dir * (f.startOffset + frac * f.half);
        // Stingerea exponențială spre exterior: energia e maximă lângă orb.
        const fall = Math.exp(-frac * 2.1);
        // Două sinusoide cu perioade care nu se divid - ondulația nu se repetă.
        const amp =
          energy *
          15 *
          fall *
          (0.55 + 0.45 * Math.sin(frac * 34 + (t / 95) * (dir > 0 ? 1 : -1.13)) * Math.sin(frac * 13 - t / 141));
        const y = cy + (pass ? -1 : 1) * amp * Math.sin(frac * 52 + (t / 74) * (dir > 0 ? 1 : -1));
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";
      ctx.shadowColor = rgba(p.glow, 0.5 * fade);
      ctx.shadowBlur = 10;
      ctx.globalAlpha = pass ? 0.5 : 1;
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}
